import React, { useEffect, useRef, useState } from "react";

const PATIENTS = [
  {
    id: 101,
    name: "Amit Patel",
    time: "10:00 AM",
    reason: "Hearing Aid Fitting",
  },
];

export default function RemotePatientWrapper() {
  const [view, setView] = useState("dashboard");
  const localCamRef = useRef(null);
  const remoteCamRef = useRef(null);

  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const camStreamRef = useRef(null);
  const screenStreamRef = useRef(null);

  const [loading, setLoading] = useState(false);

  const onConnect = async () => {
    // Initial connection logic
    await startScreenShare();
  };

  useEffect(() => {
    onConnect();
  }, []);

  const RTC_CONFIG = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] };

  const emit = (ev, data) => window.electronAPI?.socketEmit(ev, data);
  const on = (ev, fn) => window.electronAPI?.socketOn(ev, fn);

  const createPeerConnection = () => {
    if (pcRef.current) return pcRef.current;

    const pc = new RTCPeerConnection(RTC_CONFIG);

    pc.ontrack = (e) => {
      if (e.streams && e.streams[0] && remoteCamRef.current) {
        remoteCamRef.current.srcObject = e.streams[0];
      }
    };

    pc.onicecandidate = (e) => {
      if (e.candidate) emit("ice-candidate", e.candidate);
    };

    pc.onconnectionstatechange = () => {
      console.log("pc state:", pc.connectionState);
    };

    pcRef.current = pc;
    return pc;
  };

  // -----------------------------
  // 📌 ADDED FUNCTIONALITY
  // -----------------------------

  // 🔊 Mute / Unmute
  const toggleMute = () => {
    // Check both refs: camStream (if setup via screen share) or localStream (if setup via offer)
    const stream = camStreamRef.current || localStreamRef.current;

    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        // Toggle the 'enabled' property to mute/unmute at the source
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  // 🎥 Camera On/Off
  const toggleCamera = () => {
    const stream = camStreamRef.current || localStreamRef.current;

    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        // Toggle 'enabled' sends black frames (soft off) but keeps connection alive
        videoTrack.enabled = !videoTrack.enabled;
        setIsCameraOff(!videoTrack.enabled);
      }
    }
  };

  // 🖥 Stop Screen Share
  const stopScreenShare = async () => {
    if (screenStreamRef.current) {
      // 1. Stop all tracks in the screen stream
      screenStreamRef.current.getTracks().forEach((t) => {
        t.stop();

        // 2. Remove the track from the PeerConnection so the remote peer knows it's gone
        if (pcRef.current) {
          const senders = pcRef.current.getSenders();
          const sender = senders.find((s) => s.track === t);
          if (sender) {
            pcRef.current.removeTrack(sender);
          }
        }
      });
      screenStreamRef.current = null;
    }

    setIsScreenSharing(false);

    // 3. Renegotiate connection to update remote UI
    await makeOffer();
  };

  // 🖥 Toggle Screen Share
  const toggleScreenShare = () => {
    if (isScreenSharing) {
      stopScreenShare();
    } else {
      startScreenShare();
    }
  };

  // ❌ End Session
  const endSession = () => {
    if (camStreamRef.current) {
      camStreamRef.current.getTracks().forEach((t) => t.stop());
      camStreamRef.current = null;
    }

    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((t) => t.stop());
      screenStreamRef.current = null;
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }

    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }

    emit("end-session");
    setView("dashboard");
  };

  // -----------------------------
  // 📌 Start Screen Share
  // -----------------------------
  const startScreenShare = async () => {
    try {
      setLoading(true);
      setView("session");

      // Ensure PC exists
      const pc = createPeerConnection();

      // 1. Get Sources from Electron
      const sources = await window.electronAPI.getSources();
      if (!sources || sources.length === 0)
        throw new Error("No screen sources");

      const source =
        sources.find((s) => s.name === "Entire Screen") ||
        sources.find((s) => s.name === "Screen 1") ||
        sources[0];

      const displayInfo = await window.electronAPI.getDisplayInfo();
      const realWidth = displayInfo.width;
      const realHeight = displayInfo.height;

      // 2. Get Screen Stream
      screenStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          mandatory: {
            chromeMediaSource: "desktop",
            chromeMediaSourceId: source.id,
            minWidth: realWidth,
            maxWidth: realWidth,
            minHeight: realHeight,
            maxHeight: realHeight,
            minFrameRate: 30,
            maxFrameRate: 30,
          },
        },
      });

      // 3. Get Camera/Audio Stream (Only if we don't have it already)
      if (!camStreamRef.current) {
        camStreamRef.current = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
      }

      // 4. Add Camera Tracks to PC (safely)
      camStreamRef.current.getTracks().forEach((track) => {
        // Check if track is already added to avoid duplication errors
        const senders = pc.getSenders();
        const alreadyAdded = senders.find(
          (s) => s.track && s.track.id === track.id
        );

        if (!alreadyAdded) {
          pc.addTrack(track, camStreamRef.current);
        }
      });

      // 5. Add Screen Tracks to PC
      screenStreamRef.current.getTracks().forEach((track) => {
        pc.addTrack(track, screenStreamRef.current);
      });

      // 6. Preview Local Camera
      if (localCamRef.current) {
        localCamRef.current.srcObject = camStreamRef.current;
      }

      setIsScreenSharing(true);

      // 7. Renegotiate
      await makeOffer();

      setLoading(false);
    } catch (err) {
      console.error("Screen Share Error:", err);
      setLoading(false);
      setIsScreenSharing(false);
    }
  };

  // -----------------------------
  // 📌 Make Offer
  // -----------------------------
  const makeOffer = async () => {
    const pc = createPeerConnection();

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    emit("offer", {
      type: "offer",
      sdp: offer,
      meta: {
        screenId: screenStreamRef.current?.id || null,
        camId: camStreamRef.current?.id || localStreamRef.current?.id || null,
      },
    });
  };

  // -----------------------------
  // 📌 WebRTC Socket Listeners
  // -----------------------------
  useEffect(() => {
    on("answer", async (answer) => {
      const pc = createPeerConnection();
      await pc.setRemoteDescription(answer);
    });

    on("offer", async (offer) => {
      const pc = createPeerConnection();
      await pc.setRemoteDescription(offer);

      if (!localStreamRef.current && !camStreamRef.current) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        localStreamRef.current = stream;
        if (localCamRef.current) localCamRef.current.srcObject = stream;
        stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      }

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      emit("answer", answer);
    });

    on("ice-candidate", async (c) => {
      if (pcRef.current && pcRef.current.remoteDescription)
        await pcRef.current.addIceCandidate(c);
    });
    on("cursor-move", (data) => window.electronAPI.ipcSend("mouse-move", data));
  }, []);

  useEffect(() => {
    const handleClick = () => {
      window.electronAPI.ipcSend("mouse-click");
    };

    window.electronAPI.removeSocketListener("cursor-click");
    on("cursor-click", handleClick);

    return () => {
      window.electronAPI.removeSocketListener("cursor-click", handleClick);
    };
  }, []);

  useEffect(() => {
    const handleClick = (data) => {
      window.electronAPI.ipcSend("keyboard-event", data);
    };

    window.electronAPI.removeSocketListener("keyboard-event");
    on("keyboard-event", handleClick);

    return () => {
      window.electronAPI.removeSocketListener("keyboard-event", handleClick);
    };
  }, []);

  return (
    <div
      style={{
        height: "100vh",
        background: "#111",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          height: "60px",
          background: "#222",
          display: "flex",
          alignItems: "center",
          padding: "0 20px",
          borderBottom: "1px solid #333",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "#22c55e",
            }}
          ></div>
          <span style={{ fontWeight: "bold" }}>Connected with Support</span>
        </div>
      </div>

      {/* MAIN VIDEO AREA */}
      <div
        style={{
          flex: 1,
          display: "flex",
          gap: "20px",
          padding: "20px",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        {/* Local Camera */}
        <div
          style={{
            position: "relative",
            width: "45%",
            height: "100%",
            background: "#000",
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid #444",
          }}
        >
          <video
            ref={localCamRef}
            autoPlay
            playsInline
            muted
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              transform: "scaleX(-1)",
            }}
          />

          <div
            style={{
              position: "absolute",
              bottom: 15,
              left: 15,
              background: "rgba(0,0,0,0.6)",
              padding: "4px 10px",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            Me {isMuted && "(Muted)"} {isCameraOff && "(Video Off)"}
          </div>
        </div>

        {loading && <h2>Loading...</h2>}

        {/* Remote Camera */}
        <div
          style={{
            position: "relative",
            width: "45%",
            height: "100%",
            background: "#000",
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid #444",
          }}
        >
          <video
            ref={remoteCamRef}
            autoPlay
            playsInline
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: 15,
              left: 15,
              background: "rgba(0,0,0,0.6)",
              padding: "4px 10px",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            Support Agent
          </div>
        </div>
      </div>

      {/* FOOTER CONTROLS */}
      <div
        style={{
          height: "80px",
          background: "#222",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "16px",  
          borderTop: "1px solid #333",
        }}
      >
        {/* MUTE */}
        <ControlButton
          onClick={toggleMute}
          isActive={!isMuted}
          icon={isMuted ? <MicOffIcon /> : <MicIcon />}
          label={isMuted ? "Unmute" : "Mute"}
          color={isMuted ? "#ef4444" : "#374151"}
        />

        {/* CAMERA */}
        <ControlButton
          onClick={toggleCamera}
          isActive={!isCameraOff}
          icon={isCameraOff ? <VideoOffIcon /> : <VideoIcon />}
          label={isCameraOff ? "Start Video" : "Stop Video"}
          color={isCameraOff ? "#ef4444" : "#374151"}
        />

        {/* SCREEN SHARE */}
        <ControlButton
          onClick={toggleScreenShare}
          isActive={isScreenSharing}
          icon={<ScreenShareIcon />}
          label={isScreenSharing ? "Stop Share" : "Share Screen"}
          color={isScreenSharing ? "#22c55e" : "#374151"}
        />

        {/* END CALL */}
        <button
          onClick={endSession}
          style={{
            height: "50px",
            padding: "0 24px",
            background: "#dc2626",
            color: "white",
            border: "none",
            borderRadius: "25px",
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "14px",
          }}
        >
          End Session
        </button>
      </div>
    </div>
  );
}

// --- COMPONENTS ---
const ControlButton = ({ onClick, icon, label, color }) => (
  <button
    onClick={onClick}
    title={label}
    style={{
      width: "50px",
      height: "50px",
      borderRadius: "50%",
      background: color,
      border: "none",
      color: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "transform 0.1s",
    }}
    onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.95)")}
    onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
  >
    {icon}
  </button>
);

// ICONS BELOW…
const MicIcon = () => (
  <svg
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);
const MicOffIcon = () => (
  <svg
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);
const VideoIcon = () => (
  <svg
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);
const VideoOffIcon = () => (
  <svg
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
  </svg>
);
const ScreenShareIcon = () => (
  <svg
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <path d="M8 21h8" />
    <path d="M12 17v4" />
  </svg>
);
